import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { acceptInviteRequest } from '@redirector/shared';
import { Logo } from '../components/Logo.js';
import { useAuth } from '../auth/useAuth.js';
import { apiGet, apiSend } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';

const INPUT_CLASS =
  'h-(--control-height) rounded-(--radius-md) border border-input bg-card px-(--space-3) text-ink placeholder:text-ink-subtle';

/**
 * Where an invited person sets their first password.
 *
 * Deliberately outside the authentication gate: the whole point is that the
 * visitor has no account they can sign in to yet. The token in the path is the
 * only credential, so the screen asks the server whether it is still good
 * before showing a form — an expired or spent link should say so rather than
 * take a password and then reject it.
 *
 * On success it signs them in with the password they just chose — which is
 * also the only way to be sure whose console they land in. Merely navigating
 * to `/` would drop them into whatever session the browser already had: if
 * somebody else was signed in on that machine, the newcomer would arrive in
 * that person's account. Signing in replaces the cookie, so the account they
 * end up in is always the one they just claimed.
 */
export function AcceptInviteScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const token = params.token ?? '';

  const invite = useQuery({
    queryKey: ['invite', token],
    retry: false,
    queryFn: () => apiGet<{ email: string }>(`/api/invites/${encodeURIComponent(token)}`),
  });

  const [fields, setFields] = useState({ password: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);

    const parsed = acceptInviteRequest.safeParse({ password: fields.password });
    if (!parsed.success) {
      setErrors({ password: parsed.error.issues[0]?.message ?? 'Choose a password' });
      return;
    }
    /* Checked here and nowhere else: the confirmation is a typing safeguard for
       the person at the keyboard, not something the server needs to know
       about. */
    if (fields.confirm !== fields.password) {
      setErrors({ confirm: 'This does not match the password above' });
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await apiSend<{ claimed: true }>(
        'POST',
        `/api/invites/${encodeURIComponent(token)}`,
        parsed.data,
      );
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not set that password');
      setBusy(false);
      return;
    }

    /*
     * The password is set; from here nothing can un-set it, so a failure to
     * sign in is reported as exactly that and the visitor is sent to the form
     * to try it themselves rather than being told the whole thing failed.
     */
    try {
      await signIn(invite.data?.email ?? '', parsed.data.password);
    } catch {
      // Deliberately swallowed: the account exists and the password works.
    } finally {
      setBusy(false);
      void navigate('/', { replace: true });
    }
  }

  return (
    <main className="login-field grid min-h-screen place-items-center px-(--space-4) py-(--space-6)">
      <div className="w-full max-w-[26rem]">
        <div className="flex flex-col gap-(--space-5) rounded-(--radius-lg) border border-rule bg-surface-raised px-(--space-6) py-(--space-6) shadow-(--shadow-overlay)">
          <header className="flex flex-col gap-(--space-3)">
            <Logo size={40} />
            <div>
              <h1 className="text-[length:var(--text-title)] font-bold">Set your password</h1>
              {invite.data !== undefined && (
                <p className="mt-(--space-1) text-[length:var(--text-small)] font-medium text-ink-muted">
                  Redirector Engine account for{' '}
                  <span className="font-mono">{invite.data.email}</span>.
                </p>
              )}
            </div>
          </header>

          {invite.isLoading && (
            <p role="status" className="text-ink-subtle">
              Checking this invitation…
            </p>
          )}

          {invite.isError && (
            <>
              <Callout tone="danger">
                {invite.error instanceof Error
                  ? invite.error.message
                  : 'This invitation has been used or has expired'}
              </Callout>
              <p className="text-[length:var(--text-small)] font-medium text-ink-muted">
                Ask whoever invited you to send a new link.
              </p>
            </>
          )}

          {invite.data !== undefined && (
            <>
              {failure !== null && <Callout tone="danger">{failure}</Callout>}

              <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
                <Field
                  label="Password"
                  error={errors.password}
                  hint="At least 12 characters. Nobody else, including whoever invited you, can see or set it."
                >
                  {({ id, describedBy, invalid }) => (
                    <input
                      id={id}
                      type="password"
                      autoComplete="new-password"
                      autoFocus
                      className={INPUT_CLASS}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      value={fields.password}
                      onChange={(event) =>
                        setFields((prev) => ({ ...prev, password: event.target.value }))
                      }
                    />
                  )}
                </Field>

                <Field label="Confirm password" error={errors.confirm}>
                  {({ id, describedBy, invalid }) => (
                    <input
                      id={id}
                      type="password"
                      autoComplete="new-password"
                      className={INPUT_CLASS}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      value={fields.confirm}
                      onChange={(event) =>
                        setFields((prev) => ({ ...prev, confirm: event.target.value }))
                      }
                    />
                  )}
                </Field>

                <Button type="submit" variant="primary" disabled={busy}>
                  {busy ? 'Setting password…' : 'Set password and sign in'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
